import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import apiClient from '../config/axios';

dayjs.locale('it');

const ModalExpense = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    referenceMonth: dayjs(),
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome è obbligatorio';
    }
    
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Importo deve essere maggiore di 0';
    }
    
    if (!formData.referenceMonth) {
      newErrors.referenceMonth = 'Mese di riferimento è obbligatorio';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/api/finance/expenses', {
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        referenceMonth: formData.referenceMonth.toISOString(),
        note: formData.note.trim() || undefined
      });

      // Reset form
      setFormData({
        name: '',
        amount: '',
        referenceMonth: dayjs(),
        note: ''
      });
      setErrors({});
      
      onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating expense:', error);
      const errorMessage = error.response?.data?.error || 'Errore durante la creazione della spesa';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    
    // Reset form when closing
    setFormData({
      name: '',
      amount: '',
      referenceMonth: dayjs(),
      note: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <Dialog 
        open={open} 
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }} 
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Aggiungi Spesa
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome Spesa"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
              disabled={loading}
            />
            
            <TextField
              label="Importo (€)"
              type="number"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              error={!!errors.amount}
              helperText={errors.amount}
              required
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              disabled={loading}
            />
            
            <DatePicker
              label="Mese di Riferimento"
              value={formData.referenceMonth}
              onChange={(value) => handleInputChange('referenceMonth', value)}
              views={['year', 'month']}
              openTo="month"
              slotProps={{
                textField: {
                  error: !!errors.referenceMonth,
                  helperText: errors.referenceMonth,
                  required: true,
                  fullWidth: true,
                  disabled: loading
                }
              }}
            />
            
            <TextField
              label="Note (opzionale)"
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              multiline
              rows={3}
              fullWidth
              disabled={loading}
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.note.length}/500 caratteri`}
            />
            
            {errors.submit && (
              <Typography color="error" variant="body2">
                {errors.submit}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="primary"
          >
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ModalExpense;
